<template>
  <div :id="'editor' + id">
    <div class="input-group input-left">
      <input type="text" class="form-control" :class="{errorBorder: !valid}" readonly :value="validatedValueAsString" :title="validatedValueAsString" @click="$parent.expandPanel">
    </div>
    <i v-if="field.required" class="required">*</i>

    <div class="editor-panel hidden animated">
      <div class="close" @click="$parent.hidePanel(valid)">
        <span class="glyphicon glyphicon-chevron-right"></span>
      </div>
      <div class="row">
        <div class="col-md-7">
          <label class="field-name">{{title}}</label>
          <div class="has-feedback">
            <input v-model="currentValue" type="text" class="form-control" :class="{errorBorder: !valid}" @focus="enableValidating">
            <span class="form-control-feedback glyphicon glyphicon-remove" @click="emptyValue"></span>
            <label v-if="!valid" class="error">{{errorMessage}}</label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
'use strict'

import _ from 'lodash'
import editorUtils from '@/utils/editorUtils'

/**
   * Editor with which users can set the code of a project.
   */
export default {
  name: 'ProjectCodeEditor',
  props: ['field', 'functions', 'options'],

  data () {
    return {
      id: this._uid,
      currentValue: String,
      errorMessage: null,
      title: String,
      valid: true,
      initialValue: String,
      validate: false
    }
  },

  computed: {
    validatedValue: {
      get () {
        let val = editorUtils.getEditorValue(this.field)

        // overwrite current value with 'valid' value from store and set valid to true. get() is called in the following cases:
        // - when the value of this.$store.state.workingObject[this.field.key] changed (e.g. because side effect or switching workingObject)
        // - when initializing the editor
        this.setCurrentValue(val)

        return val
      },
      set (value) {
        let result = editorUtils.setEditorValue(this.field, value)
        if (result.hasChanges) {
          this.$store.commit('updateWorkingObject', result.values)
        }
      }
    },
    validatedValueAsString: {
      get () {
        return this.validatedValue === undefined ? '' : this.validatedValue + ''
      }
    },
    activeEditorValidationPending: {
      get () {
        return this.$store.state.activeEditorValidationPending
      },
      set (val) {
        this.$store.commit('setActiveEditorValidationPending', val)
      }
    }
  },

  watch: {
    'currentValue': function (value) {
      if (this.validate) {
        // set activeEditorValidationPending to true, so that navigation is blocked until validation is done
        this.activeEditorValidationPending = true

        this.validateCurrentValue(this.currentValue)
      }
    }
  },

  mounted () {
    this.title = this.field.title
  },

  methods: {
    setCurrentValue (value) {
      const replacedValue = value.replace(/\s+/g, '_') // Replaces spaces with underscores
      this.currentValue = replacedValue
      this.valid = true
    },
    emptyValue () {
      this.currentValue = ''
    },

    enableValidating () {
      if (!this.validate) this.initialValue = this.validatedValue
      this.validate = true
    },

    validateCurrentValue: _.debounce(function (val) {
      if (val === this.initialValue) {
        // value is the same as it was when initially loaded no validation needed
        this.valid = true
        this.validatedValue = val
        this.activeEditorValidationPending = false
      } else {
        // validate field and value using TerraIndexValidator
        this.$validateEditor(this.field, val).then(() => {
          // set valid to true and set val to validatedValue as validation has succeeded
          this.valid = true
          this.validatedValue = val
        }).catch(reason => {
          // extract reason and set valid to false
          this.valid = false
          this.errorMessage = reason.message
        }).finally(() => {
          // validation has finished
          this.activeEditorValidationPending = false
        })
      }
    }, 500),

    process () {
      this.currentValue = this.currentValue.toLocaleUpperCase().replace(/ /g, '_')
    }
  }
}
</script>
